import { useGetStudioLogoImage } from '~entities/studios/api/get-studio-logo-image';

import { LoginForm } from '~features/profile/ui/login-form';

interface LoginPageProps {
  locationId: string;
  redirect?: string;
}

export const LoginPage = ({ locationId, redirect }: LoginPageProps) => {
  const studioLogoQuery = useGetStudioLogoImage({ studioId: locationId });
  const logoSrc = studioLogoQuery.data?.imageUrl;
  const logoAlt =
    studioLogoQuery.data?.studioName != null
      ? `${studioLogoQuery.data.studioName} Logo`
      : 'Enrollio Logo';

  return (
    <div className='flex min-h-screen flex-col lg:flex-row'>
      {/* Left Panel - Branding */}
      <div className='relative hidden overflow-hidden bg-secondary lg:flex lg:w-[35%] xl:w-[30%]'>
        {/* Subtle gradient overlay */}
        <div className='absolute inset-0 bg-gradient-to-br from-navy-blue via-navy-blue to-[#001220]' />

        {/* Content */}
        <div className='relative z-10 flex w-full flex-col justify-between px-12 py-2 xl:px-16'>
          <div />
          <div className='space-y-8'>
            {/* Logo */}
            <div>
              <img
                src={
                  logoSrc ??
                  'https://storage.googleapis.com/enrollio-logo/enrollio-logo-white.png'
                }
                alt={logoAlt || 'Enrollio Logo'}
                className='h-20 w-auto'
              />
            </div>

            {/* Headline */}
            <div className='space-y-2'>
              <h1 className='text-4xl font-bold leading-tight text-white xl:text-5xl'>
                Your studio,
                <br />
                <span className='text-primary'>simplified.</span>
              </h1>
            </div>

            {/* Subtitle */}
            <p className='max-w-md text-lg leading-relaxed text-white/70'>
              Manage attendance, classes, and events with ease. Built for dance
              and performing arts studios.
            </p>
          </div>
          <div>
            <a
              href='https://enrollio.ai/privacy-policy'
              className='text-sm text-[#8b9aaa] transition-colors hover:text-[#b5c2dd]'
              target='_blank'
              rel='noreferrer'
            >
              Privacy Policy
            </a>
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className='flex flex-1 items-center justify-center bg-white px-6 py-10 lg:w-[55%] lg:px-16'>
        <div className='w-full max-w-[420px]'>
          {/* Mobile Logo */}
          <div className='mb-10 flex justify-center lg:hidden'>
            <img
              src={
                logoSrc ??
                'https://storage.googleapis.com/enrollio-logo/enrollio-logo-white.png'
              }
              alt={logoAlt || 'Studio Logo'}
              className='h-12 w-auto'
            />
          </div>

          <LoginForm locationId={locationId} redirect={redirect} />
        </div>
      </div>
    </div>
  );
};
